/* eslint-disable no-magic-numbers */
import { isPointInArea, IPointerPosition, IPointerArea, countPosition, getAreaCenter, movePointByAngle } from "~/util/pointer";

export interface IControlPointer {
	type?: "red" | "blue";
	testAreaInc?: number;
	angleCorrection?: number;
	snapHUDtoScreen?: number;
	padding?: number;
	showCaption?: boolean;
	countAlwaysShow?: boolean;
	onClick?: () => void;
}

export interface IControlPositionData {
	position: IPointerPosition;
	caption?: string;
	alwaysShow?: boolean;
}

interface IType {
	dimensions: {
		width: number;
		height: number;
		padding: number;
		backWidth: number;
		backHeight: number;
	};
	backColor: string;
}

const TYPES: {
	BLUE: IType;
	RED: IType;
} = {
	BLUE: {
		dimensions: {
			// rozmery celeho svg
			width: 88,
			height: 88,
			// odsazeni kapicky od okraje
			padding: 2,
			// sirka a vyska pozadi
			backWidth: 27,
			backHeight: 44,
		},
		backColor: "#00A8FF",
	},

	RED: {
		dimensions: {
			// rozmery celeho svg
			width: 88,
			height: 88,
			// odsazeni kapicky od okraje
			padding: 2,
			// sirka a vyska pozadi
			backWidth: 27,
			backHeight: 44,
		},
		backColor: "#DC1515",
	},
};

export default class ControlPointer {
	_options: IControlPointer;

	_alwaysShow: boolean;

	_dom: {
		container: HTMLElement;
		cover: SVGElement;
		back: SVGElement;
		caption: HTMLParagraphElement;
	};

	_onClick: () => void;

	_position: IPointerPosition;

	_positionData: (() => IControlPositionData) | null;

	_area: IPointerArea;

	_hudElems: Array<HTMLElement>;

	_caption: string;

	_variant: IType;

	constructor({
		type = "red",
		testAreaInc = 7,
		angleCorrection = 90,
		snapHUDtoScreen = 0,
		padding = 0,
		showCaption = false,
		countAlwaysShow = false,
		onClick = () => {},
	}: IControlPointer) {
		this._options = {
			type,
			testAreaInc,
			angleCorrection,
			snapHUDtoScreen,
			showCaption,
			countAlwaysShow,
			padding,
		};
		this._onClick = onClick;
		this._position = {
			x: 0,
			y: 0,
		};
		this._positionData = null;
		this._area = {
			width: 0,
			height: 0,
		};
		this._alwaysShow = false;
		this._hudElems = [];
		this._caption = "";
		this._variant = this._options.type === "blue"
			? TYPES.BLUE
			: TYPES.RED;
		this._dom = {
			...this._build(),
		};

		this._hide();
	}

	get container() {
		return this._dom.container;
	}

	set onClick(onClick: () => void) {
		this._onClick = onClick;
	}

	setDimension(width: number, height: number) {
		this._area.width = width;
		this._area.height = height;
	}

	// IControlPositionData
	setPosition(data: (() => IControlPositionData) | null) {
		this._positionData = data;
		this._hide();

		if (this._positionData) {
			this.redraw();
		}
	}

	setHUDElems(hudElems: Array<HTMLElement>) {
		this._hudElems = hudElems;
	}

	redraw() {
		if (!this._positionData) {
			return;
		}

		const positionData = this._positionData();

		if (!positionData.position) {
			return;
		}

		// priradime
		this._position = positionData.position;
		this._alwaysShow = positionData.alwaysShow;
		this._caption = positionData.caption;

		// vysledny uhel
		const middlePoint = getAreaCenter(this._area);
		const angle = Math.round(Math.atan2(this._position.y - middlePoint.y, this._position.x - middlePoint.x) / Math.PI * 180) + this._options.angleCorrection;
		let showCaption = this._options.showCaption;
		let pos: null | IPointerPosition = null;

		if (isPointInArea(this._position, this._area)) {
			pos = null;
		} else {
			// spocteme pozici
			pos = countPosition({
				areaSize: this._area,
				hudElems: this._hudElems,
				point: this._position,
				snapHUDtoScreen: this._options.snapHUDtoScreen,
			});
		}

		// neni spocitana pozice + vzdy zobrazit -> pozice kapky
		if (!pos && this._alwaysShow) {
			if (this._options.countAlwaysShow) {
				pos = countPosition({
					areaSize: this._area,
					hudElems: this._hudElems,
					point: this._position,
					snapHUDtoScreen: this._options.snapHUDtoScreen,
				});
			} else {
				pos = getAreaCenter(this._area);
			}

			// vzdalenost 0 -> nechceme popisek
			showCaption = false;
		}

		if (pos) {
			this._setPosition(pos, angle);

			// zobrazeni vzdalenosti
			if (showCaption) {
				this._setCaption(pos, angle);
			}

			// zobrazit kapicku
			this._show();
		} else {
			// skryt kapicku
			this._hide();
		}
	}

	_build() {
		const xmlns = "http://www.w3.org/2000/svg";
		const xlink = "http://www.w3.org/1999/xlink";
		// cele svg
		const cover = document.createElementNS(xmlns, "svg");

		cover.setAttributeNS(xmlns, "xlink", xlink);
		cover.setAttributeNS(null, "width", this._variant.dimensions.width.toString());
		cover.setAttributeNS(null, "height", this._variant.dimensions.height.toString());
		cover.setAttribute("class", "pointer-svg");
		// set-attribue x classlist.add - v ie nefunguje class list nad svg !!!!

		// pozadi - geometrie
		const back = document.createElementNS(xmlns, "g");

		back.innerHTML = `<g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
			<g transform="translate(0.000000, -1.000000)">
				<path d="M13.5,45 C20.9558441,45 27,38.9558441 27,31.5 C27,24.0441559 13.5,0.983039856 13.5,0.983039856 C13.5,0.983039856 0,24.0441559 0,31.5 C0,38.9558441 6.04415588,45 13.5,45 Z" fill="${this._variant.backColor}"></path>
				<g transform="translate(4.000000, 22.000000)" fill="#FFFFFF" >
					<circle cx="9.5" cy="9.5" r="8.5"></circle>
				</g>
			</g>
		</g>`;
		back.addEventListener("click", () => {
			this._onClick();
		});
		cover.appendChild(back);

		// container
		const container = document.createElement("div");

		container.classList.add("control-pointer");

		// popisek u vzdalenosti
		let caption: null | HTMLParagraphElement = null;

		if (this._options.showCaption) {
			caption = document.createElement("p");
			caption.innerHTML = "";
			caption.classList.add("caption");
			caption.addEventListener("click", () => {
				this._onClick();
			});
			container.appendChild(caption);
		}

		container.appendChild(cover);

		return {
			container,
			cover,
			back,
			caption,
		};
	}

	_show() {
		this._dom.container.classList.add("show");
	}

	_hide() {
		this._dom.container.classList.remove("show");
	}

	_setPosition(pos: IPointerPosition, angle: number) {
		const dim = this._variant.dimensions;
		const halfSvgW = Math.floor(dim.width / 2);
		const halfSvgH = Math.floor(dim.height / 2);
		const point = {
			x: pos.x - halfSvgW,
			y: pos.y - halfSvgH,
		};

		// nastaveni paddingu
		this._setPadding(point, angle);

		this._dom.cover.style.left = `${Math.round(point.x)}px`;
		this._dom.cover.style.top = `${Math.round(point.y)}px`;

		const halfBackW = dim.backWidth / 2;
		const tran = `translate(${halfSvgW - halfBackW}, ${halfSvgH}) rotate(${angle} ${halfBackW} 0)`;

		this._dom.back.setAttribute("transform", tran);
	}

	_setPadding(point: IPointerPosition, angle: number) {
		const dim = this._variant.dimensions;
		const moveVector = movePointByAngle({
			x: 0,
			y: dim.padding + this._options.padding,
		}, angle);

		point.x += moveVector.x;
		point.y += moveVector.y;

		return moveVector;
	}

	_setCaption(pos: IPointerPosition, angle: number) {
		const dim = this._variant.dimensions;
		const caption = this._caption;
		// zobrazeni
		const halfBackW = dim.backWidth / 2;
		const circleY = Math.round(dim.backHeight - halfBackW) + dim.padding + this._options.padding;
		const dropAnchor = movePointByAngle({
			x: 0,
			y: circleY,
		}, angle);

		this._dom.caption.style.left = `${Math.round(pos.x + dropAnchor.x)}px`;
		this._dom.caption.style.top = `${Math.round(pos.y + dropAnchor.y)}px`;
		this._dom.caption.innerHTML = caption;

		// leva a prava strana
		this._dom.caption.classList.remove("left");
		this._dom.caption.classList.remove("right");

		if (angle >= 0 && angle <= 180) {
			this._dom.caption.classList.add("left");
		} else {
			this._dom.caption.classList.add("right");
		}
	}
}
